import { defer, delay, map, merge, Observable, of, switchAll, take, tap } from "rxjs";
import * as Sentry from "@sentry/angular";
import { TranslationService } from "@app/kernel/translations/translation.service";
import { inject, Injector, untracked } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import AuthUserModelInterface from "@app/kernel/shared/interfaces/auth-user-model.interface";
import { AuthService } from "@app/kernel/tools/services/auth.service";

/**
 * Builds a fully localized {@link Sentry.ReportDialogOptions} configuration object.
 *
 * This function customizes the Sentry Report Dialog based on:
 * - The current authenticated user (pre-filling name and email if available).
 * - The active application language.
 * - Localized UI strings resolved through the {@link TranslationService}.
 *
 * @param user - The currently authenticated user, or `null`/`undefined` if no user is logged in.
 * @param translationService - The translation service instance for resolving localized labels.
 * @returns A {@link Sentry.ReportDialogOptions} object containing localized labels,
 * placeholders, and user details.
 *
 * @example
 * ```ts
 * const options = buildReportDialogOptions(currentUser, translationService);
 * Sentry.showReportDialog(options);
 * ```
 */
function buildReportDialogOptions(
  user: AuthUserModelInterface | null | undefined,
  translationService: TranslationService
): Sentry.ReportDialogOptions {
  return {
    user: {
      name: user?.name,
      email: user?.email,
    },
    // `untracked` ensures the current language is read once, avoiding reactive tracking.
    lang: untracked(translationService.currentLang),

    // Localized UI labels and messages
    title: translationService.instant("SENTRY.REPORT_DIALOG.TITLE"),
    subtitle: translationService.instant("SENTRY.REPORT_DIALOG.SUBTITLE"),
    subtitle2: translationService.instant("SENTRY.REPORT_DIALOG.SUBTITLE_2"),
    labelName: translationService.instant("SENTRY.REPORT_DIALOG.LABEL_NAME"),
    labelEmail: translationService.instant("SENTRY.REPORT_DIALOG.LABEL_EMAIL"),
    labelSubmit: translationService.instant("SENTRY.REPORT_DIALOG.LABEL_SUBMIT"),
    labelClose: translationService.instant("SENTRY.REPORT_DIALOG.LABEL_CLOSE"),
    labelComments: translationService.instant("SENTRY.REPORT_DIALOG.LABEL_COMMENTS"),
    errorGeneric: translationService.instant("SENTRY.REPORT_DIALOG.ERROR_GENERIC"),
    errorFormEntry: translationService.instant("SENTRY.REPORT_DIALOG.ERROR_FORM_ENTRY"),
    successMessage: translationService.instant("SENTRY.REPORT_DIALOG.SUCCESS_MESSAGE"),
  };
}

/**
 * Initializes the Sentry Report Dialog integration and configures it
 * to automatically show a localized, user-aware feedback form whenever
 * an error event with an `event_id` is captured.
 *
 * This initializer:
 * - Waits until translations for the `"root"` namespace are loaded.
 * - Listens for changes in both authenticated user and active language.
 * - Rebuilds {@link Sentry.ReportDialogOptions} reactively when either changes.
 * - Hooks into Sentry's event processing pipeline to trigger the dialog
 *   automatically when applicable.
 *
 * @returns An {@link Observable} that completes immediately after setup.
 *
 * @example
 * ```ts
 * sentryReportDialogInitializer().subscribe();
 * ```
 *
 * @remarks
 * The Report Dialog is displayed automatically by Sentry whenever an event with
 * an `event_id` is processed. The localized and user-aware configuration ensures
 * the dialog reflects the current app state (user and language).
 */
export function sentryReportDialogInitializer(): Observable<unknown> {
  const authService = inject(AuthService);
  const translationService = inject(TranslationService);
  const injector = inject(Injector);

  return defer(() => {
    if (!Sentry.isEnabled()) return of(void 0);

    let currentUser: AuthUserModelInterface | null | undefined = undefined;
    let options: Sentry.ReportDialogOptions | null = null;

    // Reactively track changes in user and language
    const userChange$ = authService.user$.pipe(tap(user => (currentUser = user)));
    const langChange$ = toObservable(translationService.currentLang, { injector });
    const changes$ = merge(userChange$, langChange$);

    /**
     * Add a Sentry event processor that intercepts captured exceptions
     * and automatically opens the Report Dialog for the user.
     */
    Sentry.getClient()?.addEventProcessor(event => {
      if (event.exception && event.event_id) {
        Sentry.showReportDialog({ eventId: event.event_id, ...options });
      }
      return event;
    });

    /**
     * Wait for the root translation namespace to load before
     * building the initial dialog options.
     * Rebuild options whenever user or language changes.
     */
    translationService
      .waitUntilTranslationLoaded("root")
      .pipe(
        take(1),
        map(() => changes$),
        switchAll(),
        delay(250)
      )
      .subscribe(() => (options = buildReportDialogOptions(currentUser, translationService)));

    return of(void 0);
  });
}
